import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  AssetDto,
  AssetVersionDto,
  CompleteMultipartUploadBodyDto,
  GalleryDto,
  UserAssetUploadBodyDto,
} from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  Observable,
  Subscriber,
  filter,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { OutOfStorageDialogComponent } from '../account/dialogs/out-of-storage-dialog/out-of-storage-dialog.component';
import { billingScopes, getBytesFromMegabytes } from '../billing-consts';
import { UploadFile, UploadObj, UploadObjError } from '../classes/upload-obj.class';
import {
  maxConcurrentUploadFileSize,
  maxConcurrentUploads,
  maxFileSize,
  maxGifSize,
  maxPostAssetRetryCount,
  maxUploadFullRetryCount,
  maxUploadRetryCount,
  multiPartUploadConcurrency,
  preGenerateOnUploadSizes,
} from '../consts';
import { sortAssetSection } from '../gallery/lib/sort-asset-section';
import { GROWTHBOOK } from '../growthbook';
import { canDisplayAsset } from '../lib/can-display-asset';
import { verifyFileStillExists } from '../lib/get-and-resize-image-file';
import { getFileContentType } from '../lib/get-file-type';
import { randomString } from '../lib/random-string';
import { removeExtension } from '../lib/remove-extension';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { isFreeSubscription } from '../lib/subscription-helpers';
import { ForkJoinWithConcurrencyLimit } from '../lib/throttled-fork-join';
import { PICFLOW_CHECKOUT } from '../picflow-checkout';
import { PICFLOW_CONFIG } from '../picflow-config';
import { AssetVersionUrlPipe } from '../shared/pipes/asset-version-url.pipe';
import { AssetPreGenerateService } from './asset-pre-generate.service';
import { AssetService } from './asset.service';
import { BoxService } from './box.service';
import { ChecklistService } from './checklist.service';
import { DialogService } from './dialog.service';
import { type DropboxService } from './dropbox.service';
import { DuplicateAssetService } from './duplicate-asset.service';
import { type GoogleDriveService } from './google-drive.service';
import { NetworkService } from './network.service';
import { PermissionService } from './permission.service';
import { ProgressToastService } from './progress-toast.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { SubscriptionService } from './subscription.service';
import { StorageInfo, TenantStorageService } from './tenant-storage.service';
import { ToastService } from './toast.service';
import { UpdateService } from './update.service';
import { UploadAnalyticsService } from './upload-analytics.service';
import { type FromUrlFile, type UploadFromUrlService } from './upload-from-url.service';

type UploadObserver = Subscriber<
  | { type: 'created' | 'uploaded'; data: { asset?: AssetDto; version?: AssetVersionDto; upload: UploadObj } }
  | { type: 'init'; uploads: UploadObj[] }
  | { type: 'remove'; data: { upload: UploadObj } }
>;

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private config = inject(PICFLOW_CONFIG);
  private growthBook = inject(GROWTHBOOK);
  private http = inject(HttpClient);
  private dialog = inject(DialogService);
  private assetService = inject(AssetService);
  private socketService = inject(SocketService);
  private networkService = inject(NetworkService);
  private state = inject(StateService);
  private toasts = inject(ToastService);
  private duplicateService = inject(DuplicateAssetService);
  private checklistService = inject(ChecklistService);
  private updateService = inject(UpdateService);
  private checkoutService = inject(PICFLOW_CHECKOUT);
  private uploadAnalytics = inject(UploadAnalyticsService);
  private permissionService = inject(PermissionService);
  private progressToastService = inject(ProgressToastService);
  private assetPreGenerateService = inject(AssetPreGenerateService);
  private tenantStorageService = inject(TenantStorageService);
  private subscriptionService = inject(SubscriptionService);
  private assetVersionUrl = inject(AssetVersionUrlPipe);
  public alternativeUploadServices: {
    source: 'dropbox' | 'google-drive' | 'url' | 'box';
    service: DropboxService | GoogleDriveService | UploadFromUrlService | BoxService;
  }[] = [];

  private pendingUploads: { upload: UploadObj; resolve: (value: unknown) => void }[] = [];
  private activeUploads: Set<UploadObj> = new Set();
  public uploads: UploadObj[] = [];
  public uploads$ = new BehaviorSubject<UploadObj[]>(this.uploads);
  public currentUploadSpeed = signal(0);
  public preGenerated$ = new BehaviorSubject<AssetDto>(undefined);

  public toUploadCache: File[];

  private startAllowedPending() {
    if (this.activeUploads.size >= maxConcurrentUploads) return;
    if (Array.from(this.activeUploads).reduce((t, u) => t + u.file.size, 0) >= maxConcurrentUploadFileSize) return;
    if (!this.pendingUploads.length) return;
    const winner = this.pendingUploads.shift();
    this.activeUploads.add(winner.upload);
    winner.resolve(true);
    this.startAllowedPending();
  }

  private async onUploadAllowed(upload: UploadObj) {
    const promise = new Promise(resolve => this.pendingUploads.push({ upload, resolve }));
    this.startAllowedPending();
    return promise;
  }

  private markUploadAsError(upload: UploadObj, error: UploadObjError = 'general') {
    upload.status = 'error';
    upload.error = error;
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this.uploads$.next([...this.uploads]);
    this.startAllowedPending();
  }

  private markUploadAsCancelled(upload: UploadObj) {
    upload.status = 'error';
    upload.error = 'cancelled';
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this.uploads$.next([...this.uploads]);
    this.startAllowedPending();
  }

  public async markUploadAsUploading(upload: UploadObj) {
    upload.status = 'started';
    upload.progress = 0;
    this.uploads$.next([...this.uploads]);
  }

  private multiUploadPartCompleted(upload: UploadObj, portion: number) {
    upload.progress = Math.min(100, upload.progress + 100 * portion);
    this.uploads$.next([...this.uploads]);
  }

  public markUploadAsDone(upload: UploadObj, asset?: AssetDto) {
    upload.timings.finished_at = Date.now();
    this.activeUploads.delete(upload);
    upload.status = 'done';
    upload.progress = 100;
    this.uploads$.next([...this.uploads]);
    this.startAllowedPending();

    if (asset) {
      this.pregenerateAssetOnCompletion(asset).subscribe(() => {
        this.preGenerated$.next(asset);
      });
    }
  }

  private pregenerateAssetOnCompletion(_asset: AssetDto) {
    return from(this.waitForAssetProcessing(_asset.id)).pipe(
      take(1),
      map(asset => preGenerateOnUploadSizes.map(size => this.calcAssetUrl(asset, size))),
      switchMap(urls =>
        forkJoinSafe(urls.filter(Boolean).map(url => from(this.assetPreGenerateService.preloadAsset(url))))
      )
    );
  }

  private calcAssetUrl(asset: AssetDto, longEdge: number) {
    if (!canDisplayAsset(asset)) return undefined;
    const version = asset.version_data;
    return this.assetVersionUrl.transform(version, asset, version.status, longEdge, longEdge, false);
  }

  public async waitForAssetProcessing(assetId: string): Promise<AssetDto> {
    this.socketService.connect();
    return new Promise(resolve => {
      this.socketService.onSocketEvent
        .pipe(
          filter(data => {
            if (!data) return false;
            const asset = data.payload?.asset as AssetDto;
            if (!asset) return false;
            if (asset.id !== assetId) return false;
            if (data.name === 'asset.deleted') return true;
            if (data.name === 'asset.updated') return asset.version_data.status === 'completed';
            return false;
          }),
          take(1)
        )
        .subscribe(data => {
          const asset = data.payload?.asset as AssetDto;
          return resolve(asset);
        });
    });
  }

  public removeUploads(galleryId: string) {
    this.uploads = this.uploads.filter(upload => upload.galleryId !== galleryId);
    this.uploads$.next([...this.uploads]);
  }

  public cancelUploads(galleryId: string) {
    this.uploads.filter(upload => upload.galleryId === galleryId).forEach(upload => (upload.cancelled = true));
    this.uploads$.next([...this.uploads]);
    this.removeUploads(galleryId);
  }

  public allowedFileType(file: UploadFile): boolean {
    if (this.growthBook.isOn('non-preview-assets')) return true;
    if (!file) return false;

    const contentType = getFileContentType(file);
    const contentTypeEnd = contentType.split('/')[1]?.toLowerCase();

    const extension = file.name.split('.').pop().toLowerCase() || contentTypeEnd;
    if (!extension) return false;

    const allowedRawTypes = this.growthBook.getFeatureValue('allowed_upload_file_types_raw', []);
    const allowedVideoTypes = this.growthBook.getFeatureValue('allowed_upload_file_types_video', []);

    if (
      allowedRawTypes.includes(extension) &&
      !this.permissionService.canDisplayElement(billingScopes.asset.uploadRaw)
    ) {
      if (!this.dialog.dialog$.value) {
        console.warn('File format not allowed due to plan', extension);
        this.checkoutService.open(billingScopes.asset.uploadRaw);
      }
      this.uploads.splice(
        this.uploads.findIndex(upload => upload.file === file),
        1
      );
      this.uploads$.next([...this.uploads]);
      return false;
    }

    if (allowedVideoTypes.includes(extension) && !this.permissionService.canDisplayElement(billingScopes.asset.video)) {
      if (!this.dialog.dialog$.value) {
        console.warn('File format not allowed due to plan', extension);
        this.checkoutService.open(billingScopes.asset.video);
      }
      this.uploads.splice(
        this.uploads.findIndex(upload => upload.file === file),
        1
      );
      this.uploads$.next([...this.uploads]);
      return false;
    }

    const allowedExtensions = [
      ...this.growthBook.getFeatureValue('allowed_upload_file_types', []),
      ...this.growthBook.getFeatureValue('allowed_upload_file_types_document', []),
      ...this.growthBook.getFeatureValue('allowed_upload_file_types_non_preview', []),
      ...allowedVideoTypes,
      ...allowedRawTypes,
    ];

    return allowedExtensions.includes(extension);
  }

  public async uploadUserAsset(file: File): Promise<string> {
    const params: UserAssetUploadBodyDto = {
      content_type: file.type,
      content_length: file.size,
    };
    const response = <{ upload_url: string; url: string }>(
      await lastValueFrom(this.http.post(this.config.apiEndpoint + '/assets/upload/user_asset', params))
    );
    await lastValueFrom(this.http.put(response.upload_url, file));
    return response.url;
  }

  public async uploadTenantAsset(file: UploadFile, tenantId = this.state.activeTenant$?.value?.id): Promise<string> {
    // const params: UserAssetUploadBodyDto = {
    //   content_type: file.type,
    //   content_length: file.size,
    // };
    // const response = <{ upload_url: string; url: string }>(
    //   await lastValueFrom(this.http.post(this.config.apiEndpoint + '/assets/upload/tenant_asset', params))
    // );

    const createUrl = `${this.config.apiEndpoint}/assets/upload/tenant_asset`;
    if ('source' in file) {
      try {
        let url: string;

        const uploadService = this.alternativeUploadServices.find(s => s.source === file.source)?.service;
        if (uploadService) {
          url = await (uploadService as UploadFromUrlService).uploadFileSimple(
            file as FromUrlFile,
            createUrl,
            tenantId
          );
        } else {
          console.warn('Unknown source', file);
        }

        return url;
      } catch (err) {
        console.error(err);
        return;
      }
    } else {
      const params: UserAssetUploadBodyDto = {
        content_type: file.type,
        content_length: file.size,
      };
      const response = <{ upload_url: string; url: string }>await lastValueFrom(this.http.post(createUrl, params));
      await lastValueFrom(this.http.put(response.upload_url, file));
      return response.url;
    }
  }

  public async handleCanUpload(files: File[]) {
    const storage = await firstValueFrom(this.tenantStorageService.getStorage());
    const resp = this.canUpload(files, storage);
    if (!resp) return;
    if (!resp.possible) {
      this.dialog.open(OutOfStorageDialogComponent);
      return false;
    }
    return true;
  }

  public calcNewPositions(assets: AssetDto[], files: UploadFile[], sorting: string) {
    if (sorting === 'manual') {
      let highest = Math.max(...assets.map(asset => asset.position));
      const map = new Map();
      files.forEach(file => {
        highest += 1;
        map.set(file.name, highest);
      });
      return map;
    }
    const allAssets = assets.concat(
      files.map(
        file =>
          <AssetDto>{
            id: randomString(12),
            position: 9999999999,
            version_data: { asset_name: file.name, created_at: Date.now() },
          }
      )
    );
    const sortedAssets = sortAssetSection(allAssets, sorting || 'manual');
    const map: Map<string, number> = new Map();
    const positions = this.assetService.calcOrderChanges(sortedAssets);

    sortedAssets.forEach((asset, i) => {
      const position = positions.find(pos => pos.asset.id === asset.id);
      map.set(asset.version_data.asset_name, position?.pos ?? i);
    });

    return map;
  }

  public async uploadSingle(file: File, gallery: GalleryDto, sectionId: string) {
    const upload = new UploadObj(file, gallery.id, sectionId);

    if (!(await this.handleCanUpload([file]))) {
      this.markUploadAsError(upload, 'invalid-file-type');
      return;
    }

    if (!this.allowedFileType(upload.file)) {
      this.markUploadAsError(upload, 'invalid-file-type');
      return;
    }

    if (upload.file.size > maxFileSize) {
      this.markUploadAsError(upload, 'file-too-big');
      return;
    }
    const extension = file.name.split('.').pop().toLowerCase();
    if (extension === 'gif' && upload.file.size > maxGifSize) {
      this.markUploadAsError(upload, 'file-too-big');
      return;
    }

    this.uploads.push(upload);
    this.uploads$.next([...this.uploads]);
    this.markUploadAsUploading(upload);
    upload.timings.asset_creation_started_at = Date.now();
    const asset = await this.assetService.postAsset({
      galleryId: gallery.id,
      sectionId,
      contentLength: upload.file.size,
      name: upload.file.name,
      position: upload.position,
    });
    upload.timings.asset_creation_finished_at = Date.now();
    await firstValueFrom(this.uploadAutoMethod(asset.version_data, upload));
    this.markUploadAsDone(upload, asset);
    return asset;
  }

  public async uploadNewVersion(asset: AssetDto, file: File) {
    const subscription = await firstValueFrom(this.subscriptionService.getSubscription());
    const isFree = isFreeSubscription(subscription);
    const upload = new UploadObj(file, <string>asset.gallery, <string>asset.section);
    upload.overrideName = asset.version_data.asset_name;
    this.uploads.push(upload);
    this.uploads$.next([...this.uploads]);
    this.markUploadAsUploading(upload);
    let version: AssetVersionDto;
    try {
      upload.timings.asset_creation_started_at = Date.now();
      version = await this.assetService.postVersion(
        asset,
        {
          contentLength: file.size,
          name: file.name,
          galleryId: upload.galleryId,
        },
        isFree
      );
      upload.timings.asset_creation_finished_at = Date.now();
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code !== 'unsupported_asset_type') throw err;
      this.markUploadAsError(upload, 'wrong-type');
      return;
    }

    await firstValueFrom(this.uploadAutoMethod(version, upload));
    this.markUploadAsDone(upload);
    return version;
  }

  private async createAssetOrVersion(
    upload: UploadObj,
    gallery: GalleryDto,
    sectionId: string,
    isFree: boolean,
    assetToReplace?: AssetDto
  ): Promise<{ asset: AssetDto; version: AssetVersionDto; error?: UploadObjError }> {
    try {
      if (assetToReplace) {
        const version = await this.createAssetVersionWithRetries(upload, assetToReplace, isFree);
        return { asset: undefined, version };
      } else {
        const asset = await this.createAssetWithRetries(upload, gallery, sectionId);
        return { asset, version: undefined };
      }
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code === 'resource_missing') {
        return { asset: undefined, version: undefined, error: 'resource_missing' };
      }
      if (err.error.code === 'unprocessable_entity') {
        return { asset: undefined, version: undefined, error: 'unprocessable_entity' };
      }
      return { asset: undefined, version: undefined };
    }
  }

  private async createAssetWithRetries(
    upload: UploadObj,
    gallery: GalleryDto,
    sectionId: string,
    retries = 0
  ): Promise<AssetDto> {
    if (retries) await lastValueFrom(this.networkService.online$);
    try {
      upload.timings.asset_creation_started_at = Date.now();
      const asset = await this.assetService.postAsset({
        galleryId: gallery.id,
        sectionId,
        contentLength: upload.file.size,
        name: upload.overrideName || upload.file.name,
        position: upload.position,
      });
      upload.timings.asset_creation_finished_at = Date.now();
      return asset;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code === 'unprocessable_entity') {
        throw err;
      }
      if (retries >= maxPostAssetRetryCount) throw err;
      return this.createAssetWithRetries(upload, gallery, sectionId, retries + 1);
    }
  }

  private async createAssetVersionWithRetries(
    upload: UploadObj,
    assetToReplace: AssetDto,
    isFree: boolean,
    retries = 0
  ): Promise<AssetVersionDto> {
    if (retries) await lastValueFrom(this.networkService.online$);
    try {
      upload.timings.asset_creation_started_at = Date.now();
      const version = await this.assetService.postVersion(
        assetToReplace,
        {
          contentLength: upload.file.size,
          name: upload.file.name,
          galleryId: upload.galleryId,
        },
        isFree
      );
      upload.timings.asset_creation_finished_at = Date.now();
      return version;
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (retries >= maxPostAssetRetryCount) throw err;
      return this.createAssetVersionWithRetries(upload, assetToReplace, isFree, retries + 1);
    }
  }

  private async uploadFileWithRetries(version: AssetVersionDto, upload: UploadObj, retries = 0): Promise<void> {
    if (retries) await lastValueFrom(this.networkService.online$);
    try {
      await firstValueFrom(this.uploadAutoMethod(version, upload));
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (retries >= maxUploadRetryCount) throw err;
      console.error('S3_UPLOAD_ERROR', err.status, err.error, err);
      return this.uploadFileWithRetries(version, upload, retries + 1);
    }
  }

  private async managedUploadSingle(
    observer: UploadObserver,
    upload: UploadObj,
    isFree: boolean,
    assetToReplace?: AssetDto,
    gallery?: GalleryDto,
    sectionId?: string,
    retries = 0
  ): Promise<AssetDto> {
    if (!this.allowedFileType(upload.file)) {
      this.markUploadAsError(upload, 'invalid-file-type');
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    if (upload.cancelled) {
      this.markUploadAsCancelled(upload);
      return;
    }

    if (upload.file.size > maxFileSize) {
      this.markUploadAsError(upload, 'file-too-big');
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    if (!(await verifyFileStillExists(upload.file))) {
      this.markUploadAsError(upload, 'cant-read-file');
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    const extension = upload.file.name.split('.').pop().toLowerCase();
    if (extension === 'gif' && upload.file.size > maxGifSize) {
      this.markUploadAsError(upload, 'file-too-big');
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    this.markUploadAsUploading(upload);
    if (retries && !assetToReplace) observer.next({ type: 'remove', data: { upload } });

    let asset: AssetDto;

    const file = upload.file;

    if ('source' in file) {
      try {
        let resp: AssetDto;
        const uploadService = this.alternativeUploadServices.find(s => s.source === file.source)?.service;
        if (uploadService) {
          resp = await uploadService.uploadFile(
            upload.file as never,
            gallery.id,
            sectionId,
            upload.position,
            assetToReplace,
            assetToReplace
              ? `v${await this.assetService.calcNextVersionNumber(assetToReplace.id, gallery.id, isFree)}`
              : undefined
          );
        } else {
          console.warn('Unknown source', upload.file);
        }
        if (!assetToReplace && resp) {
          asset = resp;
          observer.next({ type: 'created', data: { asset, upload } });
        }
      } catch (err) {
        console.error(err);
        this.markUploadAsError(upload, 'failed-to-upload');
        observer.next({ type: 'remove', data: { upload } });
        return;
      }
    } else {
      const {
        asset: _asset,
        version,
        error,
      } = await this.createAssetOrVersion(upload, gallery, sectionId, isFree, assetToReplace);
      asset = _asset;
      if (!version && !asset) {
        if (error === 'resource_missing') {
          this.cancelUploads(gallery.id);
          return;
        }

        this.markUploadAsError(upload, error || 'failed-to-create-asset');
        observer.next({ type: 'remove', data: { upload } });
        return;
      }

      if (upload.cancelled) {
        this.markUploadAsCancelled(upload);
        return;
      }

      if (asset) {
        observer.next({ type: 'created', data: { asset, upload } });
      }

      try {
        await this.uploadFileWithRetries(asset?.version_data || version, upload);
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (retries >= maxUploadFullRetryCount) {
          console.error(err);
          this.markUploadAsError(upload, 'failed-to-upload');
          observer.next({ type: 'remove', data: { upload } });
          return;
        }
        return this.managedUploadSingle(observer, upload, isFree, assetToReplace, gallery, sectionId, retries + 1);
      }
    }

    this.markUploadAsDone(upload, asset);
    observer.next({ type: 'uploaded', data: { asset, upload } });
    return asset;
  }

  private async internalManagedUpload(
    observer: UploadObserver,
    _files: UploadFile[],
    gallery: GalleryDto,
    sectionId: string,
    allAssets: AssetDto[],
    sorting: string
  ) {
    // Getting files / checking for duplicates
    const { files, assetsToReplace, rename } = await firstValueFrom(
      this.duplicateService.handleDuplicateFiles(
        _files,
        gallery.id,
        this.uploads.filter(upload => upload.galleryId === gallery.id && !['done', 'error'].includes(upload.status))
      )
    );

    if (!files?.length) {
      observer.complete();
      return;
    }
    const resp = await this.handleCanUpload(files);
    if (!resp) {
      observer.complete();
      return;
    }

    const positionMap: Map<string, number> = this.calcNewPositions(allAssets, files, sorting);
    const uploads = files.map(file => new UploadObj(file, gallery.id, sectionId, positionMap.get(file.name)));

    const uploadId = randomString(12);
    const startTime = Date.now();

    this.uploadAnalytics.reportUploadStarted(uploads, uploadId);
    const reportUploadBeforeClose = this.uploadAnalytics.reportUploadBeforeClose.bind(this.uploadAnalytics);

    window.onbeforeunload = function (event: BeforeUnloadEvent) {
      const e = event || window.event;
      if (e) e.returnValue = 'Are you sure? The upload is not completed yet.';
      reportUploadBeforeClose(uploads, uploadId);
      return 'Are you sure? The upload is not completed yet.';
    };

    uploads.sort((a, b) => a.position - b.position);
    observer.next({ type: 'init', uploads });

    if (!this.progressToastService.toasts$.value.some(t => t.type === 'upload' && t.gallery.id === gallery.id)) {
      this.progressToastService.addToast({
        id: uploadId,
        type: 'upload',
        gallery,
      });
    }

    this.uploads.push(...uploads);
    this.uploads$.next([...this.uploads]);

    const subscription = await firstValueFrom(this.subscriptionService.getSubscription());
    const isFree = isFreeSubscription(subscription);

    const assets = (
      await Promise.all(
        uploads.map(async upload => {
          const nameWithoutExtension = removeExtension(upload.file.name);

          const existingAsset = assetsToReplace.find(
            i => removeExtension(i.version_data.asset_name) === nameWithoutExtension
          );

          const overrideName = rename.find(r => r.fileName === upload.file.name);
          const assetToReplace = overrideName ? undefined : existingAsset;
          if (overrideName) upload.overrideName = overrideName.uploadName;

          await this.onUploadAllowed(upload);

          return this.managedUploadSingle(observer, upload, isFree, assetToReplace, gallery, sectionId);
        })
      )
    ).filter(img => img);

    // Checking if the user has cancelled the upload
    const cancelled = uploads.find(upload => upload.cancelled);
    if (cancelled) {
      this.uploadAnalytics.reportUploadCompleted(uploads, uploadId, startTime);
      this.toasts.add('Upload cancelled', 'success');
      await Promise.all(assets.map(asset => this.assetService.deleteAsset(asset)));
      observer.complete();
      window.onbeforeunload = null;
      return;
    }

    window.onbeforeunload = null;

    this.uploadAnalytics.reportUploadCompleted(uploads, uploadId, startTime);
    observer.complete();
    this.checklistService.markItemAsDone('uploadAssets').subscribe();
  }

  public managedUpload(
    _files: FileList | UploadFile[],
    gallery: GalleryDto,
    sectionId: string,
    allAssets: AssetDto[],
    sorting: string
  ) {
    this.updateService.subscribeGallery(gallery.id); // making sure the gallery that we are subscribed to the gallery
    return new Observable((observer: UploadObserver) => {
      const files = Array.from(_files);
      this.internalManagedUpload(observer, files, gallery, sectionId, allAssets, sorting);
    });
  }

  private canUpload(files: File[], storage: StorageInfo): { possible: boolean; over: bigint } {
    if (!storage) return { possible: true, over: 0n };
    if (!this.state.user) return;
    const toUpload = files.reduce((t, f) => t + BigInt(f.size), 0n);
    const remainingCapacity = storage.total - storage.used;
    return { possible: toUpload < remainingCapacity, over: toUpload - remainingCapacity };
  }

  public uploadAutoMethod(version: AssetVersionDto, upload: UploadObj) {
    if (upload.timings) {
      upload.timings.upload_started_at = Date.now();
    }
    let uploadObservable: Observable<unknown>;

    if (version['upload_id']) {
      uploadObservable = this.multipartUpload(version, upload);
    } else if (version['amz_fields']) {
      const formData = new FormData();
      Object.keys(version['amz_fields']).forEach(key => formData.append(key, version['amz_fields'][key]));
      formData.append('file', upload.file as File);
      uploadObservable = this.http.post(version['upload_url'], formData);
    } else {
      uploadObservable = this.http.put(version['upload_url'], upload.file);
    }

    return uploadObservable.pipe(
      tap(() => {
        if (!upload.timings) return;
        upload.timings.upload_finished_at = Date.now();
      })
    );
  }

  private getFileParts(file: File, version: AssetVersionDto): { blob: Blob; uploadUrl: string; partNumber: number }[] {
    const uploadUrls = version['upload_urls'] as { part_number: number; upload_url: string }[];
    const partCount = uploadUrls.length;
    const fileParts: { blob: Blob; uploadUrl: string; partNumber: number }[] = [];

    const multiPartUploadPartSize = this.getMultiPartChunkSize(file.size, partCount);

    for (let i = 0; i < partCount; i++) {
      const start = i * multiPartUploadPartSize;
      const end = Math.min(start + multiPartUploadPartSize, file.size);
      const part = file.slice(start, end);
      fileParts.push({ blob: part, uploadUrl: uploadUrls[i].upload_url, partNumber: uploadUrls[i].part_number });
    }
    return fileParts;
  }

  getMultiPartChunkSize(contentLength: number, partCount: number): number {
    const possibleChunkSizes = [
      Number(getBytesFromMegabytes(10n)),
      Number(getBytesFromMegabytes(100n)),
      Number(getBytesFromMegabytes(250n)),
    ];

    for (const size of possibleChunkSizes) {
      const calculatedPartCount = Math.floor(contentLength / size) + 1;
      if (calculatedPartCount === partCount) {
        return size;
      }
    }
    return possibleChunkSizes[possibleChunkSizes.length - 1];
  }

  private multipartUpload(version: AssetVersionDto, upload: UploadObj): Observable<unknown> {
    const fileParts = this.getFileParts(upload.file as File, version);
    upload.timings.parts = fileParts.map(part => {
      return { started_at: undefined, finished_at: undefined, size: part.blob.size };
    });
    return ForkJoinWithConcurrencyLimit(
      fileParts.map((part, i) => {
        return of(undefined).pipe(
          switchMap(() => {
            if (upload.cancelled) return of(null);
            upload.timings.parts[i].started_at = Date.now();
            return this.http.put<void>(part.uploadUrl, part.blob, { observe: 'response' }).pipe(
              tap(() => {
                this.multiUploadPartCompleted(upload, 1 / fileParts.length);
                upload.timings.parts[i].finished_at = Date.now();
              }),
              map(response => {
                const etag = response.headers.get('ETag').replace(/"/g, '');
                return { ETag: etag, PartNumber: part.partNumber };
              })
            );
          })
        );
      }),
      multiPartUploadConcurrency
    ).pipe(
      switchMap(parts => {
        if (upload.cancelled) {
          this.markUploadAsCancelled(upload);
          return this.http.post(`${this.config.apiEndpoint}/multipart_uploads/abort`, {
            key: version['original_key'],
            upload_id: version['upload_id'],
          });
        }
        const params: CompleteMultipartUploadBodyDto = {
          key: version['original_key'],
          upload_id: version['upload_id'],
          parts,
        };
        return this.http.post(`${this.config.apiEndpoint}/multipart_uploads/complete`, params);
      })
    );
  }
}
